import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ArrowRight, Menu, X } from "lucide-react";
import { useAuth, startLogin } from "@/context/AuthContext";
import { useConsent } from "@/lib/consent";

const LEGAL = [["/westmont", "Westmont Hub"], ["/what-you-need", "What you'll need"], ["/privacy", "Privacy policy"], ["/terms", "Terms of use"], ["/cookies", "Cookie policy"]];

const MobileCTA = () => {
  const { user } = useAuth();
  const consent = useConsent();
  const { pathname } = useLocation();
  if (!consent || ["/login", "/thank-you"].includes(pathname)) return null;
  return (
    <div data-testid="sticky-mobile-cta" className="fixed inset-x-0 bottom-0 z-50 border-t border-slate-200 bg-white/95 p-3 backdrop-blur lg:hidden">
      {user ? (
        <Link to="/app" data-testid="sticky-cta-dashboard" className="btn-teal w-full justify-center">Go to my dashboard <ArrowRight className="h-4 w-4" /></Link>
      ) : (
        <button onClick={startLogin} data-testid="sticky-cta-start" className="btn-teal w-full justify-center">Start free: assess my IP <ArrowRight className="h-4 w-4" /></button>
      )}
    </div>
  );
};

const NAV = [["/how-it-works", "How it works"], ["/ip-protection", "IP protection"], ["/grants-funding", "Grants & funding"], ["/pricing", "Pricing"], ["/expert-support", "Expert support"], ["/faqs", "FAQs"], ["/contact", "Contact"]];

export const Logo = ({ light, small }) => {
  const { settings } = useAuth();
  const custom = settings.platform_name && settings.platform_name !== "Protect & Fund";
  return (
    <Link to="/" data-testid="logo-link" aria-label={`${settings.platform_name} home`} className={`flex items-center gap-2 font-display text-lg font-bold ${light ? "text-white" : "text-[#0B192C]"}`}>
      {custom ? (
        <><img src="/brand/pf-icon.png" alt="" className="h-8 w-8" />{settings.platform_name}</>
      ) : (
        <img src="/brand/pf-logo.png" alt={settings.platform_name} className={small ? "h-7 w-auto" : "h-9 w-auto"} data-testid="brand-logo" />
      )}
    </Link>
  );
};

const PARTNERS = [
  ["uwl", "University of West London", "/brand/uwl-logo.svg", "https://www.uwl.ac.uk/"],
  ["charter-hcp", "Charter HCP", "/brand/charter-hcp.png", null],
  ["albion-dominion", "Albion Dominion Corporate Partners", "/brand/albion-dominion.png", null],
];

export const PartnerMark = ({ compact }) => (
  <div data-testid="partner-marks" className={`flex flex-wrap items-center ${compact ? "gap-2" : "gap-3"}`} aria-label="In partnership with">
    {!compact && <span className="caption w-full sm:w-auto">In partnership with</span>}
    {PARTNERS.map(([id, name, src, href]) => {
      const img = <img src={src} alt={name} className={compact ? "h-4" : "h-6"} />;
      return href
        ? <a key={id} href={href} target="_blank" rel="noreferrer" data-testid={`partner-${id}`} className="uwl-chip">{img}</a>
        : <span key={id} data-testid={`partner-${id}`} className="uwl-chip">{img}</span>;
    })}
  </div>
);

const AuthButton = () => {
  const { user } = useAuth();
  return user ? (
    <Link to="/app" data-testid="nav-dashboard-link" className="btn-teal btn-sm">Dashboard</Link>
  ) : (
    <button data-testid="nav-signin-button" onClick={startLogin} className="btn-teal btn-sm">Sign in</button>
  );
};

export default function PublicLayout({ children }) {
  const [open, setOpen] = useState(false);
  const { settings } = useAuth();
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="glass sticky top-0 z-40 border-b border-slate-200/70">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          <Logo />
          <nav className="hidden items-center gap-6 lg:flex" aria-label="Main">
            {NAV.map(([to, l]) => (
              <NavLink key={to} to={to} data-testid={`nav-${to.slice(1)}`} className={({ isActive }) => `text-sm transition-colors ${isActive ? "font-semibold text-[#1F8A8B]" : "text-slate-600 hover:text-[#1E2A33]"}`}>{l}</NavLink>
            ))}
            <AuthButton />
          </nav>
          <button className="text-[#1E2A33] lg:hidden" aria-label="Open menu" data-testid="mobile-menu-button" onClick={() => setOpen(!open)}>{open ? <X /> : <Menu />}</button>
        </div>
        {open && (
          <nav className="border-t border-slate-200 bg-white px-4 pb-4 lg:hidden" aria-label="Mobile">
            {NAV.map(([to, l]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="block py-2.5 text-slate-700">{l}</Link>)}
            <div className="pt-2"><AuthButton /></div>
          </nav>
        )}
      </header>
      <main className="flex-1">{children}</main>
      <MobileCTA />
      <footer className="border-t border-slate-200 bg-[#F7FAFB] pb-20 text-slate-600 lg:pb-0">
        <div className="mx-auto grid max-w-7xl gap-8 px-4 py-14 sm:px-6 md:grid-cols-3 lg:px-8">
          <div className="space-y-4">
            <Logo />
            <PartnerMark />
            <p className="text-sm text-slate-400">Guidance and preparation tools. Not a law firm and not a government service. No outcome is guaranteed.</p>
          </div>
          <div className="grid grid-cols-2 gap-2 text-sm">
            {[...NAV, ...LEGAL].map(([to, l]) => <Link key={to} to={to} data-testid={`footer-link-${to.slice(1)}`} className="hover:text-[#1F8A8B]">{l}</Link>)}
          </div>
          <p className="text-xs leading-relaxed text-slate-400">
            Funding data is drawn from official sources including GOV.UK Find a Grant (Open Government Licence v3.0). Always confirm details on the official source before applying.
            <br />© {new Date().getFullYear()} {settings.company_name || settings.platform_name}
          </p>
        </div>
      </footer>
    </div>
  );
}
